import dsgvoData from '../data/pages/dsgvo.json';
import homeData from '../data/pages/home.json';
import impressumData from '../data/pages/impressum.json';
import Fuse from 'fuse.js';

export interface ContentSection {
  id: string;
  title: string;
  content: string[];
  image?: string;
}

export interface PageContent {
  page: string;
  path: string;
  title: string;
  quote?: string;
  subtitle?: string;
  description?: string;
  lead?: string;
  sections?: ContentSection[];
  [key: string]: any;
}

export interface SearchableItem {
  id: string;
  sectionId?: string;
  page: string;
  path: string;
  title: string;
  content: string;
  originalContent: string;
  snippet: string;
}

// Convert DSGVO data to PageContent format
const convertDSGVOToPageContent = (): PageContent => {
  const deData = dsgvoData.de;
  const enData = dsgvoData.en;
  
  return {
    page: 'dsgvo',
    path: '/dsgvo',
    title: 'DSGVO / GDPR',
    subtitle: deData.intro,
    sections: [
      ...deData.sections.map((section: any) => ({
        id: section.id,
        title: section.title || '',
        content: [
          section.content || '',
          section.content2 || '',
          section.content3 || '',
          section.content4 || '',
          section.content5 || ''
        ].filter(Boolean)
      })),
      ...enData.sections.map((section: any) => ({
        id: `${section.id}-en`,
        title: section.title || '',
        content: [
          section.content || '',
          section.content2 || '',
          section.content3 || '',
          section.content4 || '',
          section.content5 || ''
        ].filter(Boolean)
      }))
    ]
  };
};

// Convert Home page data to PageContent format
const convertHomeToPageContent = (): PageContent => {
  const deData = homeData.de;

  return {
    page: 'home',
    path: '/',
    title: 'Kamana Yoga',
    subtitle: deData.hero.subtitle,
    description: deData.hero.tagline,
    sections: [
      {
        id: 'home',
        title: deData.hero.title,
        content: [deData.hero.subtitle, deData.hero.location, deData.hero.tagline]
      },
      {
        id: 'intro',
        title: 'Kamana Yoga',
        content: [deData.intro.text1, deData.intro.text2]
      },
      {
        id: 'yoga-outdoor',
        title: deData['yoga-outdoor'].heading,
        content: [deData['yoga-outdoor'].description, deData['yoga-outdoor'].experience, deData['yoga-outdoor'].dates]
      },
      {
        id: 'yoga-online',
        title: deData['yoga-online'].heading,
        content: [deData['yoga-online'].description, ...deData['yoga-online'].features]
      },
      {
        id: 'testimonials',
        title: deData.testimonials.heading,
        content: deData.testimonials.items.map((item: any) => `${item.text} - ${item.author}`)
      },
      {
        id: 'newsletter',
        title: deData.newsletter.heading,
        content: [deData.newsletter.question, deData.newsletter.motto]
      },
      {
        id: 'contact',
        title: deData.contact.heading,
        content: [deData.contact.description]
      }
    ]
  };
};

// Convert Impressum data to PageContent format
const convertImpressumToPageContent = (): PageContent => {
  const deData = impressumData.de;
  const enData = impressumData.en;
  
  return {
    page: 'impressum',
    path: '/impressum',
    title: 'Impressum / Legal Notice',
    subtitle: deData.intro,
    sections: [
      {
        id: 'intro',
        title: deData.title,
        content: [deData.intro]
      },
      {
        id: 'company-name',
        title: deData.companyName,
        content: [deData.companyName]
      },
      {
        id: 'company-info',
        title: 'Unternehmensinformationen',
        content: [
          deData.companyInfo.business,
          deData.companyInfo.member,
          deData.companyInfo.authority,
          deData.companyInfo.profession,
          deData.companyInfo.country
        ].filter(Boolean)
      },
      {
        id: 'media-owner',
        title: deData.mediaOwner.title,
        content: [
          deData.mediaOwner.name,
          deData.mediaOwner.editorial
        ].filter(Boolean)
      },
      {
        id: 'liability',
        title: deData.liability.title,
        content: [
          deData.liability.content1,
          deData.liability.content2,
          deData.liability.content3
        ].filter(Boolean)
      },
      {
        id: 'copyright',
        title: deData.copyright.title,
        content: [deData.copyright.content]
      },
      // English sections
      {
        id: 'intro-en',
        title: enData.title,
        content: [enData.intro]
      },
      {
        id: 'company-name-en',
        title: enData.companyName,
        content: [enData.companyName]
      },
      {
        id: 'company-info-en',
        title: 'Company Information',
        content: [
          enData.companyInfo.business,
          enData.companyInfo.member,
          enData.companyInfo.authority,
          enData.companyInfo.profession,
          enData.companyInfo.country
        ].filter(Boolean)
      },
      {
        id: 'media-owner-en',
        title: enData.mediaOwner.title,
        content: [
          enData.mediaOwner.name,
          enData.mediaOwner.editorial
        ].filter(Boolean)
      },
      {
        id: 'liability-en',
        title: enData.liability.title,
        content: [
          enData.liability.content1,
          enData.liability.content2,
          enData.liability.content3
        ].filter(Boolean)
      },
      {
        id: 'copyright-en',
        title: enData.copyright.title,
        content: [enData.copyright.content]
      }
    ]
  };
};

// All page data
const allPages: PageContent[] = [
  convertHomeToPageContent(),
  convertImpressumToPageContent(),
  convertDSGVOToPageContent()
];

export const useContent = () => {
  const getPageContent = (page: string): PageContent => {
    const homePage = convertHomeToPageContent();
    const impressumPage = convertImpressumToPageContent();
    const dsgvoPage = convertDSGVOToPageContent();
    const pageMap: Record<string, PageContent> = {
      home: homePage,
      '': homePage,
      '/': homePage,
      impressum: impressumPage,
      '/impressum': impressumPage,
      dsgvo: dsgvoPage,
      '/dsgvo': dsgvoPage
    };
    return pageMap[page] || homePage;
  };

  // Build searchable index from all content
  const getSearchIndex = (): SearchableItem[] => {
    const index: SearchableItem[] = [];

    // Page path mappings
    const pathMap: Record<string, string> = {
      'home': '/',
      '': '/',
      'impressum': '/impressum',
      'dsgvo': '/dsgvo'
    };

    // Index all pages dynamically
    allPages.forEach(pageData => {
      const pageKey = pageData.page.toLowerCase();
      const path = pathMap[pageKey] || pageData.path || `/${pageKey}`;
      
      // Main page entry
      const mainContent = [
        pageData.title,
        pageData.quote || '',
        pageData.subtitle || '',
        pageData.description || '',
        pageData.lead || '',
        pageData.intro || '',
        (pageData.content as any)?.intro || '',
        (pageData.content as any)?.title || '',
        (pageData.content as any)?.subtitle || '',
        (pageData.content as any)?.openingHours || ''
      ].filter(Boolean).join(' ');

      index.push({
        id: `${pageKey}-main`,
        page: pageData.title,
        path: path,
        title: pageData.title,
        content: mainContent.toLowerCase(),
        originalContent: mainContent,
        snippet: pageData.subtitle || pageData.lead || pageData.quote || pageData.intro || ''
      });

      // Index sections
      pageData.sections?.forEach(section => {
        const sectionContent = `${section.title} ${section.content.join(' ')}`;
        index.push({
          id: `${pageKey}-${section.id}`,
          sectionId: section.id,
          page: pageData.title,
          path: path,
          title: section.title,
          content: sectionContent.toLowerCase(),
          originalContent: sectionContent,
          snippet: section.content[0]
        });
      });
    });

    return index;
  };

  // Search function with fuzzy matching (typo-tolerant)
  const search = (query: string): SearchableItem[] => {
    if (!query.trim()) return [];

    const index = getSearchIndex();

    // Configure Fuse.js for fuzzy search
    const fuse = new Fuse(index, {
      keys: [
        { name: 'title', weight: 0.4 },
        { name: 'content', weight: 0.3 },
        { name: 'originalContent', weight: 0.3 }
      ],
      threshold: 0.3,
      distance: 50,
      minMatchCharLength: 3,
      includeScore: true,
      ignoreLocation: true
    });

    // Perform fuzzy search
    const fuseResults = fuse.search(query);
    
    // Convert Fuse results to our format
    const results = fuseResults.map(result => result.item);

    // Enhance results with context-aware snippets
    return results.map(item => {
      const snippetWithContext = generateContextSnippet(item.originalContent, query, item.snippet);
      return {
        ...item,
        snippet: snippetWithContext
      };
    });
  };

  // Generate a snippet showing context around the search term (fuzzy-aware)
  const generateContextSnippet = (content: string, searchTerm: string, fallbackSnippet: string): string => {
    const lowerContent = content.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    // Try exact match first
    let index = lowerContent.indexOf(lowerSearchTerm);

    // If no exact match, try finding similar words (fuzzy)
    if (index === -1) {
      const words = content.split(/\s+/);
      const fuse = new Fuse(words.map((word, idx) => ({ word, idx, pos: content.indexOf(word) })), {
        keys: ['word'],
        threshold: 0.3,
        minMatchCharLength: 3,
        includeScore: true
      });
      
      const fuzzyMatch = fuse.search(searchTerm);
      if (fuzzyMatch.length > 0 && fuzzyMatch[0].item.pos !== -1) {
        index = fuzzyMatch[0].item.pos;
      } else {
        return fallbackSnippet;
      }
    }

    // Get context around the search term (70 chars before, 70 after)
    const contextBefore = 70;
    const contextAfter = 70;
    
    const start = Math.max(0, index - contextBefore);
    const end = Math.min(content.length, index + searchTerm.length + contextAfter);
    
    let snippet = content.substring(start, end);
    
    // Add ellipsis if truncated
    if (start > 0) snippet = '...' + snippet;
    if (end < content.length) snippet = snippet + '...';
    
    return snippet;
  };

  // Scroll to section and highlight text
  const scrollToSection = (sectionId?: string, searchTerm?: string) => {
    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          if (searchTerm) {
            highlightText(element, searchTerm);
          }
        }
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Highlight text in element
  const highlightText = (container: HTMLElement, searchTerm: string) => {
    // Remove previous highlights
    const existingHighlights = document.querySelectorAll('.search-highlight');
    existingHighlights.forEach(el => {
      const parent = el.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(el.textContent || ''), el);
        parent.normalize();
      }
    });

    // Add new highlight
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    const nodesToReplace: { node: Text; index: number }[] = [];
    let node: Text | null;

    while ((node = walker.nextNode() as Text | null)) {
      const text = node.textContent || '';
      const lowerText = text.toLowerCase();
      const index = lowerText.indexOf(searchTerm.toLowerCase());
      
      if (index !== -1) {
        nodesToReplace.push({ node, index });
      }
    }

    // Replace nodes with highlighted version
    nodesToReplace.forEach(({ node, index }) => {
      const text = node.textContent || '';
      const before = text.substring(0, index);
      const match = text.substring(index, index + searchTerm.length);
      const after = text.substring(index + searchTerm.length);

      const fragment = document.createDocumentFragment();
      
      if (before) fragment.appendChild(document.createTextNode(before));
      
      const mark = document.createElement('mark');
      mark.className = 'search-highlight';
      mark.style.backgroundColor = 'rgba(40, 129, 196, 0.3)';
      mark.style.padding = '2px 4px';
      mark.style.borderRadius = '3px';
      mark.style.fontWeight = '600';
      mark.textContent = match;
      fragment.appendChild(mark);
      
      if (after) fragment.appendChild(document.createTextNode(after));

      node.parentNode?.replaceChild(fragment, node);
    });
  };

  return {
    getPageContent,
    getSearchIndex,
    search,
    scrollToSection
  };
};
